import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent } from '@/components/ui';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { UserAvatar } from '@/components/UserAvatar';
import { useAuthStore } from '@/store/auth';
import { useOrgMemberStore } from '@/store/orgMember';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { publicClubMembershipService } from '@/services/publicClubMembership';
import { memberPortalService } from '@/services/memberPortal';
import { isCourtHireBookingCalendarVisible, type CourtHireBookingStatus } from '@court-sync/shared';
import { Clock, IdCard, Megaphone, Settings } from 'lucide-react';

export function ClubMemberDashboardSection() {
  const { user } = useAuthStore();
  const clubSlug = useOrgMemberStore((s) => s.clubSlug);
  const orgProfile = useOrgMemberStore((s) => s.orgProfile);
  const { p } = useOrgPaths();

  const membershipQuery = useQuery({
    queryKey: ['member-portal-home', clubSlug],
    enabled: Boolean(clubSlug),
    queryFn: async () => {
      if (!clubSlug) return null;
      const res = await publicClubMembershipService.getMyMembership(clubSlug);
      return res.success ? res.data : null;
    },
  });

  const bookingsQuery = useQuery({
    queryKey: ['member-court-hire-home', clubSlug],
    enabled: Boolean(clubSlug) && orgProfile?.modules.courtHireEntitled,
    queryFn: async () => {
      if (!clubSlug) return [];
      const res = await memberPortalService.getMyCourtHireBookings(clubSlug);
      return res.success && res.data ? res.data : [];
    },
  });

  const noticesQuery = useQuery({
    queryKey: ['member-notices-home', clubSlug],
    enabled: Boolean(clubSlug),
    queryFn: async () => {
      if (!clubSlug) return [];
      const res = await memberPortalService.getNotices(clubSlug);
      return res.success && res.data ? res.data : [];
    },
  });

  const memberships = membershipQuery.data?.memberships ?? [];
  const activeMembership = memberships.find((m) => m.status === 'active');
  const upcomingBookings = (bookingsQuery.data ?? [])
    .filter((b) => isCourtHireBookingCalendarVisible(b.status as CourtHireBookingStatus))
    .filter((b) => new Date(b.startAt) >= new Date())
    .slice(0, 3);
  const unreadNotices = (noticesQuery.data ?? []).filter((n) => !n.isRead).slice(0, 3);

  const membershipPath = p('/membership');
  const courtHirePath = p('/court-hire');
  const noticesPath = p('/notices');
  const settingsPath = p('/settings');

  return (
    <div className="space-y-6">
      {user && settingsPath ? (
        <Card>
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex items-center gap-4">
              <UserAvatar
                avatarUrl={user.avatarUrl}
                firstName={user.firstName}
                lastName={user.lastName}
                size="lg"
              />
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
            </div>
            <Button asChild variant="outline" size="sm" className="shrink-0">
              <Link to={settingsPath}>
                <Settings className="mr-2 h-4 w-4" aria-hidden />
                Account settings
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4">
        <DashboardStatCard
          icon={<IdCard className="text-primary-600 dark:text-primary-400" aria-hidden />}
          iconBg="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
          label="Membership"
          mobileLabel="Member"
          value={activeMembership ? 'Active' : memberships.length > 0 ? 'Pending' : '—'}
        />
        <DashboardStatCard
          icon={<Clock className="text-tennis-600 dark:text-tennis-400" aria-hidden />}
          iconBg="bg-tennis-100 dark:bg-tennis-900"
          iconColor="text-tennis-600 dark:text-tennis-400"
          label="Upcoming courts"
          mobileLabel="Courts"
          value={upcomingBookings.length}
        />
        <DashboardStatCard
          icon={<Megaphone className="text-amber-600 dark:text-amber-400" aria-hidden />}
          iconBg="bg-amber-100 dark:bg-amber-900"
          iconColor="text-amber-600 dark:text-amber-400"
          label="Unread notices"
          mobileLabel="Notices"
          value={unreadNotices.length}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {membershipPath ? (
          <Button asChild variant="outline" size="sm">
            <Link to={membershipPath}>Membership</Link>
          </Button>
        ) : null}
        {courtHirePath && orgProfile?.modules.courtHireEntitled ? (
          <Button asChild size="sm">
            <Link to={courtHirePath}>Book court</Link>
          </Button>
        ) : null}
        {noticesPath ? (
          <Button asChild variant="outline" size="sm">
            <Link to={noticesPath}>View notices</Link>
          </Button>
        ) : null}
      </div>

      {upcomingBookings.length > 0 ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold">Upcoming court bookings</h3>
            {upcomingBookings.map((b) => (
              <div key={b.id} className="text-sm">
                <p className="font-medium">{b.courtName ?? 'Court'}</p>
                <p className="text-muted-foreground">{new Date(b.startAt).toLocaleString()}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {unreadNotices.length > 0 ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold">Recent notices</h3>
            {unreadNotices.map((n) => (
              <div key={n.id}>
                <p className="font-medium">{n.title}</p>
                <p className="text-sm text-muted-foreground line-clamp-2">{n.body}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
